import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft } from "lucide-react";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/blog/${slug}`)
      .then((res) => setPost(res.data))
      .catch(() => setError("Post not found"))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-40 text-ink/60" data-testid="blog-post-loading">
        Loading article...
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-40 text-center" data-testid="blog-post-not-found">
        <h1 className="font-display text-4xl text-forest mb-4">Article not found</h1>
        <Link to="/blog" className="text-terracotta link-underline">← Back to all articles</Link>
      </div>
    );
  }

  return (
    <article data-testid="blog-post-page" className="bg-bone">
      <SEO
        title={post.title}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        image={post.cover_image}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: post.title,
          description: post.excerpt,
          author: { "@type": "Person", name: post.author },
          datePublished: post.date,
          image: post.cover_image,
        }}
      />
      <div className="max-w-4xl mx-auto px-6 md:px-12 pt-28 md:pt-36 pb-10">
        <Link to="/blog" className="inline-flex items-center gap-2 text-forest/70 hover:text-terracotta text-sm" data-testid="back-to-blog">
          <ArrowLeft size={16} /> Back to journal
        </Link>
        <p className="overline mt-8 mb-4">
          {new Date(post.date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
        </p>
        <h1 className="font-display text-4xl md:text-6xl text-forest font-semibold leading-[1.05] tracking-tight">
          {post.title}
        </h1>
        <p className="mt-6 text-ink/70 text-lg">By {post.author}</p>
      </div>

      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <div className="aspect-[16/8] rounded-2xl overflow-hidden border border-sage/25">
          <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="prose-content font-sans text-lg text-ink/85 leading-[1.85] space-y-6 whitespace-pre-wrap">
          {post.content}
        </div>

        <div className="mt-16 pt-10 border-t border-sage/25">
          <Link to="/contact" className="inline-flex items-center bg-forest hover:bg-[#5D4B85] text-bone rounded-full px-7 py-3.5 font-medium transition-colors">
            Have questions? Reach out →
          </Link>
        </div>
      </div>
    </article>
  );
}
